import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { Button, Drawer, Form } from 'antd';
import debounce from 'lodash.debounce';
import { routePaths } from 'router';
import type { TTripTaskForCargoReq, TripRes, TripStatusType } from 'api/swagger';
import { useAddCargoToActiveTrip } from 'api/trips/use-add-cargo-to-active-trip';
import { useGetCargoTrips } from 'api/trips/use-get-cargo-trips';
import { useSelectCargoStore } from 'shared/stores/use-select-cargos';
import type { ITripFilter } from 'shared/stores/use-trip-filters';
import type { TSelectOptionChange } from 'types/select-option';
import { CargoTasksCreationModal } from 'components/cargos/cargos-select-info-widget/components/cargo-tasks-creation-modal';
import { TripFormSelect } from 'components/common/trip-form-select';
import type { IAddToTripFormFields, ICargoSelectAddToTripProps, IPendingTripData } from './types';
import { useAddCargoToTripFlow } from './use-add-cargo-to-trip-flow';

export const CargoSelectAddToTrip: React.FC<ICargoSelectAddToTripProps> = (props) => {
  const { open, setOpen } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { cargosWithSelection, deleteAll } = useSelectCargoStore();

  const [selectedTrip, setSelectedTrip] = useState<TripRes | undefined>(undefined);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [pendingTripData, setPendingTripData] = useState<IPendingTripData | null>(null);

  const { addToTrip, isLoading } = useAddCargoToTripFlow();
  const { mutateAsync: addCargoToActiveTrip, isLoading: isAddingToActiveTrip } = useAddCargoToActiveTrip();
  const { data: cargoTrips } = useGetCargoTrips(selectedTrip?.id);

  const [form] = Form.useForm<IAddToTripFormFields>();

  const tripFilters = useMemo((): ITripFilter | undefined => {
    if (cargosWithSelection.size >= 2) {
      return { statuses: ['planning'] as TripStatusType[] };
    }

    return undefined;
  }, [cargosWithSelection]);

  const navigateToTrip = (tripId: number) => {
    navigate(generatePath(routePaths.trip, { tripId: String(tripId) }));
  };

  const onClose = () => {
    setOpen(false);
    form.resetFields();
    setSelectedTrip(undefined);
  };

  const onSubmit = debounce(() => form.submit(), 200);

  const onFinish = async (values: IAddToTripFormFields) => {
    if (!values.trip || !selectedTrip) return;

    const pendingData = await addToTrip({
      tripId: values.trip,
      tripStatus: selectedTrip.status,
      cargoTrips,
    });

    if (!pendingData) {
      onClose();
      navigateToTrip(values.trip);
      return;
    }

    setPendingTripData(pendingData);
    setIsTaskModalOpen(true);
  };

  const handleChange: TSelectOptionChange<number> = (option) => {
    form.setFieldsValue({ trip: option.value });
  };

  const handleTripSelect = (trip: TripRes | undefined) => {
    setSelectedTrip(trip);
  };

  const handleTaskModalCancel = () => {
    setIsTaskModalOpen(false);
    setPendingTripData(null);
  };

  const handleTasksComplete = async (tasks: TTripTaskForCargoReq[]) => {
    if (!pendingTripData) return;

    await addCargoToActiveTrip({
      tripId: pendingTripData.tripId,
      data: {
        cargoId: pendingTripData.cargoId,
        cargoItemIds: pendingTripData.cargoItemIds,
        tripTasks: tasks,
      },
    });

    deleteAll();
    setIsTaskModalOpen(false);
    setPendingTripData(null);
    onClose();
    navigateToTrip(pendingTripData.tripId);
  };

  return (
    <>
      <Drawer
        title={t('selected:add_to_trip')}
        placement='right'
        open={open}
        extra={
          <Button type='primary' loading={isLoading} onClick={onSubmit}>
            {t('common:save')}
          </Button>
        }
        onClose={onClose}>
        <Form form={form} name='trip' layout='vertical' onFinish={onFinish}>
          <TripFormSelect
            name='trip'
            placeholder='Select Trip'
            rules={[{ required: true, message: 'Please select trip', type: 'number' }]}
            handleChange={handleChange}
            filters={tripFilters}
            onTripSelect={handleTripSelect}
          />
        </Form>
      </Drawer>
      <CargoTasksCreationModal
        open={isTaskModalOpen}
        isPending={isAddingToActiveTrip}
        onCancel={handleTaskModalCancel}
        onComplete={handleTasksComplete}
      />
    </>
  );
};
